import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import type {
  CreateDashboardClientRequest,
  DashboardClient,
  DashboardClientBooking,
  DashboardClientDetail,
  DashboardClientsParams,
  DashboardClientsResponse,
  UpdateDashboardClientRequest,
} from "@/api/types";
import { useDashboardRequest } from "./dashboard-request";

function cleanParams(params: DashboardClientsParams): Record<string, string | number> {
  const clean: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(params) as [
    string,
    string | number | undefined | null,
  ][]) {
    if (value !== undefined && value !== null && value !== "") {
      clean[key] = value;
    }
  }
  return clean;
}

export function useDashboardClients(
  params: DashboardClientsParams = {},
  options?: { enabled?: boolean },
) {
  const dashboard = useDashboardRequest();

  return useQuery({
    queryKey: dashboard.queryKey("clients", params),
    queryFn: () =>
      api
        .get("dashboard/clients", {
          ...dashboard.requestOptions(),
          searchParams: cleanParams(params),
        })
        .json<DashboardClientsResponse>(),
    placeholderData: (previous, previousQuery) => {
      const previousParams = previousQuery?.queryKey[3] as DashboardClientsParams | undefined;
      return previousQuery?.queryKey[1] === (dashboard.slug ?? "selected") &&
        previousParams?.q === params.q
        ? previous
        : undefined;
    },
    enabled: options?.enabled ?? true,
  });
}

interface ClientDetailEnvelope {
  client: DashboardClient;
  bookings: DashboardClientBooking[];
}

export function useDashboardClient(id: string) {
  const dashboard = useDashboardRequest();

  return useQuery({
    queryKey: dashboard.queryKey("clients", id),
    queryFn: () =>
      api.get(`dashboard/clients/${id}`, dashboard.requestOptions()).json<ClientDetailEnvelope>(),
    select: (data): DashboardClientDetail => ({ ...data.client, bookings: data.bookings }),
    enabled: !!id,
  });
}

interface CreateClientEnvelope {
  client: DashboardClient;
}

export function useCreateDashboardClient() {
  const queryClient = useQueryClient();
  const dashboard = useDashboardRequest();

  return useMutation({
    mutationFn: (data: CreateDashboardClientRequest) =>
      api
        .post("dashboard/clients", { ...dashboard.requestOptions(), json: data })
        .json<CreateClientEnvelope>(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

export function useUpdateDashboardClient() {
  const queryClient = useQueryClient();
  const dashboard = useDashboardRequest();

  return useMutation({
    mutationFn: ({ id, ...data }: UpdateDashboardClientRequest) =>
      api
        .patch(`dashboard/clients/${id}`, { ...dashboard.requestOptions(), json: data })
        .json<CreateClientEnvelope>(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}
